import React from 'react';
import styled from 'styled-components';
import { I } from '../../../../common/v5/config';

const ToolbarContainer = styled.div`
  position: fixed;
  bottom: var(--cention-spacing-m);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  background: white;
  border: var(--cention-border-width-base) solid var(--cention-color-border-base);
  border-radius: var(--cention-border-radius-large);
  padding: var(--cention-spacing-xs);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    bottom: var(--cention-spacing-s);
    left: var(--cention-spacing-s);
    right: var(--cention-spacing-s);
    transform: none;
  }
`;

const ExecutionSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-xs);
  padding-right: var(--cention-spacing-s);
  border-right: var(--cention-border-width-base) solid var(--cention-color-border-base);
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--cention-spacing-2xs);
  padding: var(--cention-spacing-s) var(--cention-spacing-m);
  border: none;
  border-radius: var(--cention-border-radius-base);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease-in-out;
  white-space: nowrap;
  min-height: 36px;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  i {
    font-size: 14px;
  }
  
  &.primary {
    background: var(--cention-color-primary);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--cention-color-primary-shade-1);
    }
    
    &:active {
      background: var(--cention-color-primary-shade-2);
    }
  }
  
  &.secondary {
    background: var(--cention-color-background-light);
    color: var(--cention-color-text-dark);
    border: var(--cention-border-width-base) solid var(--cention-color-border-base);
    
    &:hover:not(:disabled) {
      background: var(--cention-color-background-base);
      border-color: var(--cention-color-border-dark);
    }
  }
  
  &.danger {
    background: var(--cention-color-danger-tint-2);
    color: var(--cention-color-danger);
    border: var(--cention-border-width-base) solid var(--cention-color-danger-tint-1);
    
    &:hover:not(:disabled) {
      background: var(--cention-color-danger-tint-1);
      border-color: var(--cention-color-danger);
    }
  }
  
  &.success {
    background: var(--cention-color-success-tint-2);
    color: var(--cention-color-success);
    border: var(--cention-border-width-base) solid var(--cention-color-success-tint-1);
  }
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-2xs);
  padding: var(--cention-spacing-xs) var(--cention-spacing-s);
  border-radius: var(--cention-border-radius-base);
  font-size: 12px;
  font-weight: 500;
  
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    animation: ${props => props.status === 'running' ? 'pulse 1.5s ease-in-out infinite' : 'none'};
  }
  
  &.idle {
    background: var(--cention-color-background-light);
    color: var(--cention-color-text-light);
    
    .status-dot {
      background: var(--cention-color-text-lighter);
    }
  }
  
  &.running {
    background: var(--cention-color-warning-tint-2);
    color: var(--cention-color-warning);
    
    .status-dot {
      background: var(--cention-color-warning);
    }
  }
  
  &.success {
    background: var(--cention-color-success-tint-2);
    color: var(--cention-color-success);
    
    .status-dot {
      background: var(--cention-color-success);
    }
  }
  
  &.error {
    background: var(--cention-color-danger-tint-2);
    color: var(--cention-color-danger);
    
    .status-dot {
      background: var(--cention-color-danger);
    }
  }
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const NodeCounter = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cention-spacing-2xs);
  padding: var(--cention-spacing-xs) var(--cention-spacing-s);
  background: var(--cention-color-background-light);
  border-radius: var(--cention-border-radius-base);
  font-size: 12px;
  color: var(--cention-color-text-light);
  
  i {
    font-size: 11px;
  }
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background: var(--cention-color-border-base);
`;

const BottomToolbar = ({
  isExecuting = false,
  executionStatus = 'idle',
  nodes = [],
  onStartExecution,
  onStopExecution,
  onResetExecution,
  onClearCanvas
}) => {
  const getStatusText = () => {
    switch (executionStatus) {
      case 'running': return I('Executing...');
      case 'success': return I('Execution completed');
      case 'error': return I('Execution failed');
      default: return I('Ready');
    }
  };

  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'running': return 'fa fa-spinner fa-spin';
      case 'success': return 'fa fa-check-circle';
      case 'error': return 'fa fa-exclamation-circle';
      default: return 'fa fa-circle';
    }
  };

  const canExecute = nodes.length > 0 && !isExecuting;
  const hasExecution = executionStatus === 'success' || executionStatus === 'error';

  return (
    <ToolbarContainer>
      <ExecutionSection>
        {isExecuting ? (
          <ActionButton 
            className="secondary" 
            onClick={onStopExecution}
            title={I('Stop execution')}
          >
            <i className="fa fa-stop" />
            {I('Stop')}
          </ActionButton>
        ) : (
          <ActionButton 
            className="primary" 
            onClick={onStartExecution}
            disabled={!canExecute}
            title={canExecute ? I('Execute workflow') : I('Add nodes to execute')}
          >
            <i className="fa fa-play" />
            {I('Test workflow')}
          </ActionButton>
        )}
        
        <StatusIndicator status={executionStatus} className={executionStatus}>
          <div className="status-dot" />
          {getStatusText()}
        </StatusIndicator>
      </ExecutionSection>

      <NodeCounter>
        <i className="fa fa-sitemap" />
        {nodes.length} {nodes.length === 1 ? I('node') : I('nodes')}
      </NodeCounter>

      {hasExecution && (
        <>
          <Divider />
          <ActionButton 
            className="secondary" 
            onClick={onResetExecution}
            title={I('Clear execution data')}
          >
            <i className="fa fa-refresh" />
            {I('Reset')}
          </ActionButton>
        </>
      )}

      {nodes.length > 0 && (
        <>
          <Divider />
          <ActionButton 
            className="danger" 
            onClick={onClearCanvas}
            title={I('Clear all nodes from canvas')}
          >
            <i className="fa fa-trash" />
            {I('Clear')}
          </ActionButton>
        </>
      )}
    </ToolbarContainer>
  );
};

export default BottomToolbar; 